/* eslint-disable no-console */
// GA4 ecommerce dataLayer helpers. Pushes events in the GA4 `items` shape
// so GTM-managed GA4 Event tags can forward them to the measurement API.
//
// Events fired from JS: add_to_cart, begin_checkout. The view_item and
// purchase events are pushed server-side from
// app/views/spree/shared/_ga4_view_item.js.erb and _ga4_purchase.js.erb.
//
// Every variant in this storefront has a SKU, so `item_id` is always the
// SKU — no DB-id fallback.

const BRAND = 'Volition Beauty';
const DEFAULT_CURRENCY = 'USD';

function toNumber(value) {
  if (value == null) return 0;
  const num = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(num) ? num : 0;
}

function buildItem({
  sku, name, variant, price,
}, quantity) {
  return {
    item_id: sku,
    item_name: name,
    item_brand: BRAND,
    // Empty string when no variant text — matches the server-rendered
    // view_item / purchase events (`<%= j(nil) %>` → '') so the field is
    // shape-stable across all four GA4 ecommerce events.
    item_variant: variant || '',
    price: toNumber(price),
    quantity,
  };
}

function pushEcommerceEvent(event, ecommerce) {
  if (typeof window === 'undefined' || !window.dataLayer) return;
  try {
    // Clear previous ecommerce object so fields don't leak across events —
    // per GA4 recommended dataLayer pattern.
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event, ecommerce });
  } catch (e) {
    if (window.console && console.warn) console.warn(`[ga4] push failed (${event})`, e);
  }
}

// Plain (non-ecommerce) event push for engagement/quiz events. Params are
// spread alongside `event` rather than nested under `ecommerce`.
function pushEvent(event, params) {
  if (typeof window === 'undefined' || !window.dataLayer) return;
  try {
    window.dataLayer.push({ event, ...params });
  } catch (e) {
    if (window.console && console.warn) console.warn(`[ga4] push failed (${event})`, e);
  }
}

function trackAddToCart(product, quantity = 1) {
  if (!product) return;
  pushEcommerceEvent('add_to_cart', {
    currency: DEFAULT_CURRENCY,
    value: toNumber(product.price) * quantity,
    items: [buildItem(product, quantity)],
  });
}

function trackBeginCheckout(lineItems, orderTotal) {
  if (!lineItems || lineItems.length === 0) return;
  pushEcommerceEvent('begin_checkout', {
    currency: DEFAULT_CURRENCY,
    value: toNumber(orderTotal),
    items: lineItems.map((li) => buildItem(li, li.quantity || 1)),
  });
}

function trackViewItemList(products, { itemListId, itemListName }) {
  if (!products || products.length === 0) return;
  pushEcommerceEvent('view_item_list', {
    item_list_id: itemListId,
    item_list_name: itemListName,
    items: products.map((product, index) => ({
      ...buildItem(product, 1),
      item_list_id: itemListId,
      item_list_name: itemListName,
      index,
    })),
  });
}

function trackSelectItem(product, { itemListId, itemListName, index }) {
  if (!product) return;
  pushEcommerceEvent('select_item', {
    item_list_id: itemListId,
    item_list_name: itemListName,
    items: [{
      ...buildItem(product, 1),
      item_list_id: itemListId,
      item_list_name: itemListName,
      index,
    }],
  });
}

function trackViewCart(lineItems, orderTotal) {
  if (!lineItems || lineItems.length === 0) return;
  pushEcommerceEvent('view_cart', {
    currency: DEFAULT_CURRENCY,
    value: toNumber(orderTotal),
    items: lineItems.map((li) => buildItem(li, li.quantity || 1)),
  });
}

function trackRemoveFromCart(lineItem, quantity) {
  if (!lineItem) return;
  pushEcommerceEvent('remove_from_cart', {
    currency: DEFAULT_CURRENCY,
    value: toNumber(lineItem.price) * toNumber(quantity),
    items: [buildItem(lineItem, quantity)],
  });
}

function trackAddShippingInfo(lineItems, orderTotal, shippingTier) {
  if (!lineItems || lineItems.length === 0) return;
  pushEcommerceEvent('add_shipping_info', {
    currency: DEFAULT_CURRENCY,
    value: toNumber(orderTotal),
    shipping_tier: shippingTier || '',
    items: lineItems.map((li) => buildItem(li, li.quantity || 1)),
  });
}

function trackAddPaymentInfo(lineItems, orderTotal, paymentType) {
  if (!lineItems || lineItems.length === 0) return;
  pushEcommerceEvent('add_payment_info', {
    currency: DEFAULT_CURRENCY,
    value: toNumber(orderTotal),
    payment_type: paymentType || '',
    items: lineItems.map((li) => buildItem(li, li.quantity || 1)),
  });
}

function trackQuizStart({ quizId, entryPath }) {
  if (!quizId) return;
  pushEvent('quiz_start', {
    quiz_id: quizId,
    entry_path: entryPath,
  });
}

function trackQuizStepComplete({
  quizId, stepId, stepNumber, stepAnswer,
}) {
  if (!quizId) return;
  pushEvent('quiz_step_complete', {
    quiz_id: quizId,
    step_id: stepId,
    step_number: stepNumber,
    step_answer: stepAnswer,
  });
}

function trackQuizComplete({ quizId, stepsCompleted, recommendedSkus }) {
  if (!quizId) return;
  pushEvent('quiz_complete', {
    quiz_id: quizId,
    steps_completed: stepsCompleted,
    recommended_skus: recommendedSkus,
  });
}

// Fires when a recommended product on the quiz results page is engaged.
// Carries the standard select_item `items` shape (so the click is also
// attributable in ecommerce reporting) plus the quiz_id context.
function trackQuizRecommendationClick(product, {
  quizId, itemListId, itemListName, index,
}) {
  if (!product) return;
  if (typeof window === 'undefined' || !window.dataLayer) return;
  try {
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'quiz_recommendation_click',
      quiz_id: quizId,
      ecommerce: {
        item_list_id: itemListId,
        item_list_name: itemListName,
        items: [{
          ...buildItem(product, 1),
          item_list_id: itemListId,
          item_list_name: itemListName,
          index,
        }],
      },
    });
  } catch (e) {
    if (window.console && console.warn) console.warn('[ga4] push failed (quiz_recommendation_click)', e);
  }
}

const ga4DataLayer = {
  pushEcommerceEvent,
  trackAddToCart,
  trackBeginCheckout,
  trackViewItemList,
  trackSelectItem,
  trackViewCart,
  trackRemoveFromCart,
  trackAddShippingInfo,
  trackAddPaymentInfo,
  trackQuizStart,
  trackQuizStepComplete,
  trackQuizComplete,
  trackQuizRecommendationClick,
};

export default ga4DataLayer;
